import { DEFAULT_COURSE_PREVIEW_IMAGE } from 'components/admin/constants';
import {
  ETHENA_QUESTIONS_EMAIL,
  ETHENA_SUPPORT_EMAIL,
  PageName,
} from 'lib/constants';
import { EnvVariable, getEnv } from 'lib/helpers/envHelper';
import { Organization } from 'lib/types/company';
import _ from 'lodash';
import { ElementType } from 'react';
import { EthenaIcon, LifeLabsIcon, SequoiaOneIcon } from './icons';

export const getCoursePreviewImageSrc = (
  organization: Organization
): string => {
  switch (organization) {
    case Organization.LIFE_LABS:
      // TODO: josh - replace once they give us an image
      return '/images/lifelabs-logo-square.png';
    case Organization.ETHENA:
    default:
      return DEFAULT_COURSE_PREVIEW_IMAGE;
  }
};

export const getFaviconHref = (organization: Organization): string => {
  switch (organization) {
    case Organization.LIFE_LABS:
      return '/images/lifelabs_favicon.ico';
    case Organization.ETHENA:
    default:
      return '/favicon.ico';
  }
};

export const getHelpCenterUrl = (organization: Organization): string => {
  switch (organization) {
    case Organization.LIFE_LABS:
      return 'https://lifelabslearning.freshdesk.com/support/home';
    case Organization.ETHENA:
    default:
      return 'https://help.goethena.com/';
  }
};

export const getHomePageName = (organization: Organization): PageName => {
  switch (organization) {
    case Organization.LIFE_LABS:
      return PageName.ADMIN_EVENTS;
    case Organization.ETHENA:
    default:
      return PageName.ADMIN_DASHBOARD;
  }
};

export const getHowItWorksUrl = (organization: Organization): string => {
  switch (organization) {
    case Organization.LIFE_LABS:
      // TODO: josh - replace once they give us a url
      return 'https://www.lifelabslearning.com/';
    case Organization.ETHENA:
    default:
      return 'https://www.goethena.com/platform';
  }
};

export const getIcon = (organization: Organization): ElementType => {
  switch (organization) {
    case Organization.LIFE_LABS:
      return LifeLabsIcon;
    case Organization.SEQUOIA_ONE:
      return SequoiaOneIcon;
    case Organization.ETHENA:
    default:
      return EthenaIcon;
  }
};

export const getOrganizationFromHost = (host: string): Organization => {
  if (_.includes(host, 'lifelabs')) {
    return Organization.LIFE_LABS;
  } else if (_.includes(host, 'sequoia')) {
    return Organization.SEQUOIA_ONE;
  } else {
    return Organization.ETHENA;
  }
};

/**
 * Use this function in the 99% of cases where we need to display the _effective_ organization name.
 *
 * All organizations that aren't LifeLabs (which has a white-labeled experience) offer the standard
 * Ethena experience, so, most of the time, we just need to display `'Ethena'` as the organization name.
 *
 * @returns The effective organization name.
 */
export const getOrganizationName = (organization: Organization): string => {
  switch (organization) {
    case Organization.LIFE_LABS:
      return 'LifeLabs Learning';
    case Organization.ETHENA:
    default:
      return 'Ethena';
  }
};

/**
 * Use this function in the rare cases where we need to display the actual organization name.
 * (For example, to display the list of organizations in Internal Tools).
 *
 * @returns The actual name of the organization.
 */
export const getRealOrganizationName = (organization: Organization): string => {
  switch (organization) {
    case Organization.LIFE_LABS:
      return 'LifeLabs Learning';
    case Organization.SEQUOIA_ONE:
      return 'Sequoia One';
    case Organization.ETHENA:
      return 'Ethena';
    default:
      return _.startCase(_.toLower(organization));
  }
};

export const getQuestionsEmail = (organization: Organization): string => {
  switch (organization) {
    case Organization.LIFE_LABS:
      return 'support@lifelabslearning.com';
    case Organization.ETHENA:
    default:
      return ETHENA_QUESTIONS_EMAIL;
  }
};

export const getSupportEmail = (organization: Organization): string => {
  switch (organization) {
    case Organization.LIFE_LABS:
      return 'support@lifelabslearning.com';
    case Organization.ETHENA:
    default:
      return ETHENA_SUPPORT_EMAIL;
  }
};

export const getSurvicateKey = (organization: Organization): string => {
  switch (organization) {
    case Organization.LIFE_LABS:
      return getEnv(EnvVariable.LIFE_LABS_SURVICATE_KEY);
    case Organization.ETHENA:
    default:
      return getEnv(EnvVariable.ETHENA_SURVICATE_KEY);
  }
};

export const getTermsOfServiceUrl = (organization: Organization): string => {
  switch (organization) {
    case Organization.LIFE_LABS:
      return 'https://www.lifelabslearning.com/';
    case Organization.ETHENA:
    default:
      return '/documents/EthenaServicesAgreement.html';
  }
};

export const getLegalAndPrivacyUrl = (organization: Organization): string => {
  switch (organization) {
    case Organization.LIFE_LABS:
      return '/legal-and-privacy';
    case Organization.ETHENA:
    default:
      return 'https://www.goethena.com/legal/privacy-policy/';
  }
};
